import { DictionaryLang } from "./model";
import { TranslationsInstance } from "./TranslationsInstance";

/**
 * Provides universal i18n methods (language versions) as a singleton service in global scope (browser window or NPM thread).
 */
export class Translations {

  /**
   * Gets the singleton instance behind the static methods:
   * Can be used to access more advanced methods that are not available in static class.
   */
  public static readonly instance = new TranslationsInstance();

  /**
   * Sets the locales path from which the locales will loaded.
   * @param path The path to the directory in wwwroot where locales can be found. Default is "locales".
   */
  public static setLocalesPath(path: string): void {
    this.instance.localesPath = path?.replace(/[/]*$/gm, "") || this.instance.localesPath;
  }

  /**
   * Sets the function which is responsible for loading a dictionary for the given language.
   * This is an alternative to the default locales path functionality.
   * @param localesFunc The function that will be called to load the dictionary for the given language.
   */
  public static setLocalesFunc(localesFunc: (lang: string) => Promise<DictionaryLang> ): void {
    this.instance.localesFunc = localesFunc ;
  }

  /**
   * Translates given key to the current language.
   * @param key - Key for the text.
   * @param interpolateParams - Variables for the interpolation.
   * @param lang Language to fetch. Default is the current language.
   * @param templateOnly If true, will fetch the template directly without the interpolation.
   * @returns The translated value in current/given language, potentially interpolated.
   * If none is found...
   *
   * - If the key does not start with "SALAXY." (plain text, not a key), returns the text as-is.
   * - If the Finnish translation is found, returns it. Prefixed with red exclamation mark in test only.
   * - If fi translation not found, returns the key - always prefixed with red exclamation mark (prod and test).
   */
  public static get(key: string, interpolateParams?: any, lang?: string, templateOnly = false): string {
    return this.instance.get(key, interpolateParams, lang, templateOnly);
  }

  /**
   * Translates given key to the current language with default value.
   * @param key - Key for the text.
   * @param defaultValue - The default value that is returned if the key is not found. Null is the default.
   * @param lang Language to fetch. Default is the current language.
   * @returns The translated value in current language or if none is found the key.
   */
  public static getWithDefault(key: string, defaultValue = "", lang?: string | null): string {
    return this.instance.getWithDefault(key, defaultValue, lang);
  }


  /**
   * Gets any object (string or dictionary) with the given property path.
   * @param key Key for the text.
   * @param lang Language to fetch. Default is the current language.
   * @returns The object if found using the key, null if none is found.
   */
  public static getAny(key: string, lang?: string): any {
    return this.instance.getAny(key, lang);
  }

  /**
   * Loads a language bundle from the server on-demand.
   * The method will check the loadedLanguages collection, so it will load a language only once => Can be called every time the user language changes.
   * This method is designed to fail outside browser. Browser is recognized based on having globalThis.fetch.
   * The default Finnish dictionary is added added from code lib (not http) unless the Config has the
   * noCache attribute set (used in dev-mode to get the templates and i18n directly from files instead of dist).
   * @param lang Language to load.
   */
  public static loadLanguage(lang: string): Promise<void> {
    return this.instance.loadLanguage(lang);
  }

  /**
   * Adds a dictionary.
   * @param lang - ISO language code for the dictionary to be added: fi, en or sv.
   * @param translations - A translations object (json).
   */
  public static addDictionary(lang: string, translations: DictionaryLang): void {
    return this.instance.addDictionary(lang, translations);
  }

  /**
   * Add all dictionaries from a single language object.
   * @param translations - A translations object (json).
   * This object must have language codes as first level keys.
   */
  public static addDictionaries(translations: {[key: string]: DictionaryLang }): void {
    return this.instance.addDictionaries(translations);
  }

  /**
   * Sets the Salaxy user interface language.
   * @param lang - One of the supported languages: "fi", "en" or "sv".
   */
  public static setLanguage(lang: "fi" | "en" | "sv"): void {
    return this.instance.setLanguage(lang);
  }

  /**
   * Gets the current user interface language
   * @returns One of the supported languages: "fi", "en" or "sv".
   */
  public static getLanguage(): "fi" | "en" | "sv" {
    return this.instance.getLanguage();
  }
}
